import React, {
    FC,
    memo,
    PropsWithChildren,
    useCallback,
    useMemo,
    useState,
} from 'react';

import { Range } from 'slate';

import BasicButton from '../../../../components/buttons/basicButton';
import ComponentTypesIconWrapper from '../../../../components/componentTypesIcon/ComponentTypesIconWrapper';
import BasicModal from '../../../../components/modals/basicModal';
import Divider from '../../../../components/shared/Divider';
import useVariantsByComponentIdQuery from 'hooks/components/useVariantsByComponentIdQuery';
// components
import styled from 'styled-components';

// theme
import aampeTheme from '../../../../constants/aampeTheme';

import useComponentTypesQuery from '../../../../hooks/component-types/useComponentTypesQuery';
import useGetComponentTypeIdByVariant from '../../../../hooks/component-types/useGetComponentTypeIdByVariant';
import useLabelsVariantsQuery from '../../../../hooks/labels/useLabelsVariantsQuery';
import useVariantBulkCreateMutation from 'hooks/variants/useVariantCreateBulkMutation';

// model
import { ComponentTypeMapped } from '../../../../models/component-types-model';
import {
    LabelAlternateListModel,
    LabelWithVariantsModel,
} from '../../../../models/label.model';
import { VariantCreateBulk } from 'models/variant.model';

//helpers
import AlternatesTable from './AlternatesTable';
import LabelTable from './LabelTable';

interface LabelHistoryModalProps {
    handleClose: () => void;
    isLoading?: boolean;
    isExisting?: boolean;
    selectedElement: { [key: string]: any };
    currentSelection?: Range;
    formulaId: number;
}

const LabelHistoryModal: FC<PropsWithChildren<LabelHistoryModalProps>> = ({
    handleClose,
    isLoading = false,
    isExisting = false,
    selectedElement,
    formulaId,
}) => {
    const variantType = selectedElement.selectedElement.variantType;

    const { data: componentTypes } = useComponentTypesQuery();

    const componentTypeId = useGetComponentTypeIdByVariant(
        variantType as string,
    );

    const { data: labelsData, isLoading: labelsDataIsLoading } =
        useLabelsVariantsQuery(componentTypeId as string);
    const elementId = selectedElement.selectedElement.elementId;
    const filteredLabelsData: LabelWithVariantsModel[] | undefined =
        useMemo(() => {
            return labelsData?.filter((label) => {
                return label.alternates.length > 0;
            });
        }, [labelsData]);

    const [currentLabel, setCurrentLabel] = useState<
        LabelWithVariantsModel | undefined
    >();

    const [selectedRowData, setSelectedRowData] =
        useState<LabelAlternateListModel>({});

    const variantsBulkCreateMutation = useVariantBulkCreateMutation(
        formulaId,
        selectedElement.selectedElement.elementId,
    );
    const { data = [] } = useVariantsByComponentIdQuery(elementId);
    const currentAlternateTexts = useMemo<string[]>(() => {
        return data.map((variant) => {
            return variant.content;
        });
    }, [data]);

    const updateSelectedRows = useCallback(
        (labelName: string, selectedData: any[], event: any) => {
            // Ensure the label exists or initialize it
            const currentRowSelection = new Set(
                selectedRowData[labelName] || [],
            );

            // compare currently selected in ref and in table to find alterations in the 2 lists
            const itemsToRemove = Array.from(currentRowSelection).filter(
                (item) => !selectedData.includes(item),
            );
            const itemsToAdd = selectedData.filter(
                (item) => !currentRowSelection.has(item),
            );

            //   Update the selection by removing and adding items as necessary
            itemsToAdd.forEach((item) => {
                currentRowSelection.add(item);
            });
            itemsToRemove.forEach((item) => {
                currentRowSelection.delete(item);
            });

            //this  code reselects remaining nodes if users switch tables
            currentRowSelection.forEach((item) => {
                const rowNode = event.api.getRowNode(item.index);
                if (rowNode) {
                    rowNode.setSelected(true);
                }
            });

            // Update the ref with the new selection -sets it to empty if user is currently deselecting last selected node
            const updatedSelection =
                currentRowSelection.size === 0 && selectedData.length === 0
                    ? []
                    : Array.from(currentRowSelection);

            setSelectedRowData((prev) => ({
                ...prev,
                [labelName]: updatedSelection,
            }));
        },
        [selectedRowData],
    );

    const isDisabled = useMemo(() => {
        if (currentLabel) {
            const activeSelections = Object.values(selectedRowData);
            const hasDifference = activeSelections.some((items) =>
                items.some(
                    (item) => !currentAlternateTexts.includes(item.text),
                ),
            );
            return !hasDifference;
        } else {
            return true;
        }
    }, [currentLabel, selectedRowData, currentAlternateTexts]);

    const handleSelectionChanged = useCallback(
        (event: any) => {
            const currentLabel =
                event.api.clientSideRowModel.rowsToDisplay[0].data.label_name;
            const selectedNodes = event.api.getSelectedNodes();
            const selectedData = selectedNodes.map((node: any) => node.data);
            event.context = {
                labelName: currentLabel,
            };

            updateSelectedRows(currentLabel, selectedData, event);
        },
        [updateSelectedRows],
    );

    const handleCreateAlternates = useCallback(
        async (data: VariantCreateBulk[]) => {
            await variantsBulkCreateMutation.mutateAsync(data, {
                onSuccess: () => {
                    handleClose();
                },
            });
        },
        [variantsBulkCreateMutation, handleClose],
    );

    const handleAddAlternates = useCallback(() => {
        const newAlternates: VariantCreateBulk[] = [];
        Object.values(selectedRowData).forEach((labelType) => {
            labelType.forEach((newAlternate) => {
                newAlternates.push({
                    component_id: selectedElement.selectedElement.elementId!,
                    content: newAlternate.text,
                    label_id: newAlternate.label_id,
                });
            });
        });
        handleCreateAlternates(newAlternates);
    }, [
        formulaId,
        handleCreateAlternates,
        selectedElement,
        selectedRowData,
        variantType,
    ]);

    const variantData = useMemo(
        () => componentTypes?.map[variantType],
        [variantType, componentTypes],
    );

    const Icon = useMemo(
        () => (
            <ComponentTypesIconWrapper
                dataKey={(variantData as ComponentTypeMapped)?.data_key}
            />
        ),
        [variantData],
    );

    return (
        <BasicModal
            header="Use alternates from label’s history"
            description="You can reuse text snippets from previous messages."
            descriptionStyle={{
                padding: aampeTheme.spacing4(1, 6),
                lineHeight: aampeTheme.size.lineHeight.default,
                fontWeight: aampeTheme.weights.light,
            }}
            headerStyle={{
                background: aampeTheme.colors.white,
                textAlign: 'left',
                color: aampeTheme.colors.gray600,
                fontSize: aampeTheme.size.font.title,
                padding: aampeTheme.spacing4(6),
                paddingBottom: 0,
                display: 'flex',
            }}
            handleClose={handleClose}
            width="640px"
            childContainerStyle={{
                paddingTop: 16,
            }}
        >
            <ModalBody>
                <Divider></Divider>
                <VariantTypeContainer>
                    {Icon}
                    {`${variantType} (${filteredLabelsData?.length})`}
                </VariantTypeContainer>

                <Container>
                    <TableContainer>
                        <LabelTable
                            data={filteredLabelsData}
                            isLoading={labelsDataIsLoading}
                            setCurrentLabel={setCurrentLabel}
                        ></LabelTable>
                        <AlternatesTable
                            currentLabel={currentLabel}
                            isLoading={isLoading && labelsDataIsLoading}
                            handleSelectionChanged={handleSelectionChanged}
                            selectedRowData={selectedRowData}
                            currentAlternateTexts={currentAlternateTexts}
                        ></AlternatesTable>
                    </TableContainer>
                </Container>
                <ActionContainer>
                    <BasicButton
                        isPrimary={false}
                        name={'Cancel'}
                        isOutlined
                        action={handleClose}
                        isDisabled={isLoading}
                    />
                    <BasicButton
                        name={isExisting ? 'Save' : 'Use alternates'}
                        action={handleAddAlternates}
                        isLoading={isLoading}
                        isDisabled={isDisabled}
                    />
                </ActionContainer>
            </ModalBody>
        </BasicModal>
    );
};

const VariantTypeContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    gap: aampeTheme.spacing4(2),
});
const TableContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
});
const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
});
const ModalBody = styled.div({
    fontWeight: 'normal',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    rowGap: aampeTheme.spacing4(4),
    paddingTop: 0,
});

const ActionContainer = styled.div({
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: '100%',
    columnGap: aampeTheme.spacing4(4),
});

export default memo(LabelHistoryModal);
